import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ResultsViewProps } from '../../engine/types';
import type { HsaSavingsValues } from './config';
import type { HsaSavingsResult } from './calculate';
import { BarChart, formatDollarPrecise } from './BarChart';
import { usePlanTypeChange } from './PlanTypeField';
import { ResetButton } from './ResetButton';
import { BalanceMixCard } from './BalanceMixCard';
import { hasModule, totalContributed, totalMedicalExpenses } from './modules';
import { useCalculator } from '../../engine/state/CalculatorProvider';
import { Odometer } from '../../engine/chrome/Odometer';

const PLAN_OPTIONS: { value: HsaSavingsValues['planType']; label: string }[] = [
  { value: 'individual', label: 'Individual' },
  { value: 'family', label: 'Family' },
];

/**
 * Sliding thumb for the plan-type toggle (2026-10-06, tactile feedback).
 * Measures the selected option's label and returns the thumb's position and
 * size, so the thumb matches the comp's pill exactly at every width -- the
 * two options aren't always equal (at narrow widths the track shrinks to fit
 * "Individual" and "Family", 105px vs 81px). `ready` turns the slide
 * transition on only after the first measurement, so nothing animates on load.
 */
function usePlanThumb(planType: HsaSavingsValues['planType']) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ x: number; w: number } | null>(null);
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const measure = () => {
      // Measures the selected option's segment, not its label: the label is
      // what dips (scales) while pressed, the segment never transforms, and
      // the label fills it exactly. Fractional rects, not offset* (whole
      // pixels), so the pill's edges land exactly where the comp's did.
      // x is relative to the track's padding box (inside its 1px border).
      const segment = track.querySelector<HTMLElement>('input:checked')?.parentElement;
      if (segment) {
        // Snap edges to device pixels, as layout does for the label's own
        // box -- a translated box would otherwise sit on a half pixel and
        // anti-alias differently from the comp's pill.
        const dpr = window.devicePixelRatio || 1;
        const snap = (v: number) => Math.round(v * dpr) / dpr;
        const t = track.getBoundingClientRect();
        const s = segment.getBoundingClientRect();
        const origin = snap(t.left + track.clientLeft);
        setBox({ x: snap(s.left) - origin, w: snap(s.right) - snap(s.left) });
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    return () => ro.disconnect();
  }, [planType]);

  useEffect(() => {
    if (!box || ready) return;
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, [box, ready]);

  const style = box ? { width: box.w, transform: `translateX(${box.x}px)` } : undefined;
  return { trackRef, style, ready };
}

/**
 * "Odonlow" layout (Brad, 2026-10-01; lead designer's mockup,
 * docs/calculator_test.html). Renders before the step panel
 * (config.resultsOrder: 'before'): plan type + reset, the two totals as
 * cards, then the chart card; the six fields follow as their own card.
 *
 * Wording is prod's (legal/compliance). 2026-10-09: the mockup wording Brad
 * had chosen (total cards' labels and detail lines, "Final year" legend)
 * is commented out pending legal/compliance; it's kept on
 * branch hsa-savings-new-wording. The opt-in modules (balance-mix and Total
 * contributed cards) still use mockup wording but are off unless ticked, and
 * their checkboxes are commented out. Its look is calc-hsa-savings-odonlow.css
 * (layouts/odonlow.scss).
 */
export function HsaSavingsOdonlowResultsView({
  result,
  values,
  setValue,
  reset,
}: ResultsViewProps<HsaSavingsValues, HsaSavingsResult>) {
  const changePlanType = usePlanTypeChange(values, setValue);
  const { options } = useCalculator();
  // Optional module (Modules checkboxes on the component dialog): the
  // mockup's balance-mix card beside the chart.
  const showMix = hasModule(options, 'balance-mix');
  // Optional module: the mockup's third total card.
  const showContributed = hasModule(options, 'total-contributed');
  // Years behind the balance shown (not the field, which may be mid-edit),
  // so "After X years" always matches the number above it. Unused while
  // that line is commented out (2026-10-09).
  // const years = result.compoundedSavingsByYear.length;
  // Unique per mount: a native radio group's name is document-wide, so a
  // fixed name (as PlanTypeField uses) joins the radios of every HSA Savings
  // on the page into one group -- found 2026-10-01 with Odonlow + Legacy on
  // one local page, where checking one calculator's plan unchecked the other's.
  const planId = `hsab-calc-savings-plan-${useId().replace(/:/g, '')}`;
  const thumb = usePlanThumb(values.planType);

  return (
    <div className="hsab-calc-savings__results hsab-calc-savings__results--odonlow">
      <div className="hsab-calc-savings__odon-top">
        {/* A labelled radiogroup, not a fieldset: a <legend> can't sit beside
            the options in a flex row. */}
        <div className="hsab-calc-savings__odon-plan" role="radiogroup" aria-labelledby={`${planId}-label`}>
          <span className="hsab-calc-savings__odon-plan-label" id={`${planId}-label`}>
            Choose your plan type:
          </span>
          {/* Sliding thumb (2026-10-06): the selected option's white pill now
              slides between the two options instead of repainting in place,
              so the click visibly "does" something. See usePlanThumb. */}
          <div className="hsab-calc-savings__odon-segmented" ref={thumb.trackRef}>
            <span
              className={'hsab-calc-savings__odon-thumb' + (thumb.ready ? ' hsab-calc-savings__odon-thumb--ready' : '')}
              style={thumb.style}
              aria-hidden="true"
            />
            {PLAN_OPTIONS.map((opt) => (
              <span key={opt.value} className="hsab-calc-savings__odon-segment">
                <input
                  type="radio"
                  id={`${planId}-${opt.value}`}
                  name={planId}
                  value={opt.value}
                  checked={values.planType === opt.value}
                  onChange={() => changePlanType(opt.value)}
                />
                <label htmlFor={`${planId}-${opt.value}`}>{opt.label}</label>
              </span>
            ))}
          </div>
        </div>
        <ResetButton onClick={reset} className="hsab-calc-savings__odon-reset" />
      </div>

      <div
        className={'hsab-calc-savings__odon-metrics' + (showContributed ? ' hsab-calc-savings__odon-metrics--three' : '')}
      >
        {/* 2026-10-09: prod's labels and order (tax savings first), no detail
            lines -- any new wording needs legal/compliance first. The mockup
            wording is kept on branch hsa-savings-new-wording and commented
            out below to restore. */}
        <div className="hsab-calc-savings__odon-metric">
          {/* Hyphenated words kept whole, so a narrow card breaks where prod
              does ("*Tax-Savings on / Tax-Deferred Growth:"), not mid-word. */}
          <div className="hsab-calc-savings__odon-metric-label">
            <span style={{ whiteSpace: 'nowrap' }}>*Tax-Savings</span> on{' '}
            <span style={{ whiteSpace: 'nowrap' }}>Tax-Deferred</span> Growth:
          </div>
          <div className="hsab-calc-savings__odon-metric-value">
            <Odometer value={result.taxSavings} format={formatDollarPrecise} firstMs={1000} />
          </div>
        </div>
        <div className="hsab-calc-savings__odon-metric">
          <div className="hsab-calc-savings__odon-metric-label">Future Value of Your Health Savings Account:</div>
          <div className="hsab-calc-savings__odon-metric-value">
            <Odometer value={result.totalSavings} format={formatDollarPrecise} firstMs={1000} />
          </div>
        </div>
        {/*
        <div className="hsab-calc-savings__odon-metric">
          <div className="hsab-calc-savings__odon-metric-label">Projected HSA balance</div>
          <div className="hsab-calc-savings__odon-metric-value">
            <Odometer value={result.totalSavings} format={formatDollarPrecise} firstMs={1000} />
          </div>
          <div className="hsab-calc-savings__odon-metric-detail">
            After {years} {years === 1 ? 'year' : 'years'}
          </div>
        </div>
        <div className="hsab-calc-savings__odon-metric">
          <div className="hsab-calc-savings__odon-metric-label">Estimated tax savings</div>
          <div className="hsab-calc-savings__odon-metric-value">
            <Odometer value={result.taxSavings} format={formatDollarPrecise} firstMs={1000} />
          </div>
          <div className="hsab-calc-savings__odon-metric-detail">Income tax saved on your contributions</div>
        </div>
        */}
        {showContributed && (
          // Module (off unless ticked). As in the mockup, the detail line
          // reports the medical spending the contributions are "before".
          <div className="hsab-calc-savings__odon-metric">
            <div className="hsab-calc-savings__odon-metric-label">Total contributed</div>
            <div className="hsab-calc-savings__odon-metric-value">
              <Odometer value={totalContributed(values)} format={formatDollarPrecise} firstMs={1000} />
            </div>
            <div className="hsab-calc-savings__odon-metric-detail">
              {formatDollarPrecise(totalMedicalExpenses(values))} projected medical spending
            </div>
          </div>
        )}
      </div>

      <div className={'hsab-calc-savings__odon-charts' + (showMix ? ' hsab-calc-savings__odon-charts--mix' : '')}>
        <div className="hsab-calc-savings__odon-card hsab-calc-savings__odon-chart">
          <div className="hsab-calc-savings__odon-card-heading">
            {/* 2026-10-06: "Balance growth" title, sub-label and the "Earlier years"
                legend key commented out per content team notes (temporary; kept to restore). */}
            {/*
            <div>
              <h3 className="hsab-calc-savings__odon-card-title">Balance growth</h3>
              <p className="hsab-calc-savings__odon-card-subtitle">
                Projected year-end balance after qualified medical expenses.
              </p>
            </div>
            */}
            {/* 2026-10-09: "Final year" legend commented out -- prod's chart has
                none. (The axis titles came back 2026-10-19, built into BarChart.) */}
            {/*
            <div className="hsab-calc-savings__odon-legend">
              <span>
                <i className="hsab-calc-savings__odon-swatch hsab-calc-savings__odon-swatch--earlier" aria-hidden="true" />
                Earlier years
              </span>
              <span>
                <i className="hsab-calc-savings__odon-swatch hsab-calc-savings__odon-swatch--final" aria-hidden="true" />
                Final year
              </span>
            </div>
            */}
          </div>
          <BarChart result={result} spotlight />
        </div>
        {showMix && <BalanceMixCard values={values} result={result} />}
      </div>

      {result.noSavings && (
        <p className="hsab-calc-savings__no-savings-msg">
          The amount of average yearly medical expenses is greater than average yearly contributions, so no savings
          will accumulate.
        </p>
      )}

      {/* 2026-10-08: footnote moved out of the calculator into a separate Text
          component on the page (content team), so it isn't shown twice. Kept to restore. */}
      {/*
      <p className="hsab-calc-savings__footnote">
        *Estimate of potential income tax savings from HSA contributions and earnings.
      </p>
      */}
    </div>
  );
}
